import { useNavConfig } from "components/Providers/Nav";
import UniversalLink from "library/Loader/UniversalLink";
import { fmobile, fresponsive, ftablet } from "library/fullyResponsive";
import type { Dispatch, ReactNode, SetStateAction } from "react";
import { useMemo, useRef, useState } from "react";
import styled, { css } from "styled-components";
import colors from "styles/colors";
import links from "utils/links";

import type { HamburgerSectionLinks } from ".";
import AnimatedIcons from "../AnimatedIcons";
import LinkButton from "../Buttons/LinkButton";
import Secondary from "../Buttons/Secondary";
import DropDown from "../Dropdown";
import ResponsiveDropDown from "./HamburgerMenu";
import { graphql, useStaticQuery } from "gatsby";
import Banner from "components/Banner";

export interface Link {
  name: string;
  href: string;
  description?: string;
  icon?: ReactNode;
  background?: string;
  color?: string;
}

export interface SectionLinks {
  section: string;
  animationHandler?: Dispatch<SetStateAction<number>>;
  links?: Link[];
  columns?: SectionLinks[];
}

export default function DektopHeader({
  hamburgerData,
  open,
  setOpen,
}: {
  hamburgerData: HamburgerSectionLinks[];
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
}) {
  const [canPlayProducts, setCanPlayProducts] = useState(-1);
  const [canPlaySolutions, setCanPlaySolutions] = useState(-1);
  const wrapperEl = useRef<HTMLDivElement>(null);
  const logoLetters = useRef<SVGGElement>(null);

  const { menuDark, color } = useNavConfig();

  const letterColors = menuDark ? colors.greenDark01 : colors.white;

  const banners: Queries.BannerQuery = useStaticQuery(graphql`
    query Banner {
      allContentfulAnnouncementBannerMessage(
        sort: { messagePriority: DESC }
        filter: { id: { ne: "ee44f688-59a4-55c0-813a-c74b72ba492d" } }
      ) {
        nodes {
          id
          bannerTitle
          showMessageAt
          hideMessageAt
          messagePriority
          linkUrl
        }
      }
    }
  `);

  const allBanners = useMemo(() => {
    return banners.allContentfulAnnouncementBannerMessage.nodes.filter(
      (b) =>
        // start date either undefined or is in the past
        (b.showMessageAt === null || new Date(b.showMessageAt) < new Date()) &&
        // end date is in the future
        b.hideMessageAt &&
        new Date(b.hideMessageAt) > new Date()
    );
  }, [banners]);

  const SITE_LNKS: SectionLinks[] = useMemo(
    () => [
      {
        section: "Products",
        animationHandler: setCanPlayProducts,
        links: [
          {
            name: "Core Accounting",
            href: links.coreAccounting,
            description: "The modern general ledger that scales with you",
            icon: (
              <AnimatedIcons
                color={colors.orangePastel01}
                animation="cornerArrows"
                canPlay={canPlayProducts === 0}
              />
            ),
            background: colors.orangePastel02,
            color: colors.orangeGray02,
          },
          {
            name: "Revenue Automation",

            href: links.revenueAutomation,
            description: "Automate your end-to-end revenue process",
            icon: (
              <AnimatedIcons
                color={colors.purplePastel01}
                animation="swirlArrows"
                canPlay={canPlayProducts === 1}
              />
            ),
            background: colors.purplePastel02,
            color: colors.purpleGray02,
          },
        ],
      },
      {
        section: "Solutions",
        animationHandler: setCanPlaySolutions,
        links: [
          {
            name: "Mid-Market",
            href: links.midMarket,
            icon: (
              <AnimatedIcons
                animation="clusterArrows"
                color={colors.greenPastel01}
                canPlay={canPlaySolutions === 0}
              />
            ),
            background: colors.greenPastel02,
            color: colors.orangeGray02,
          },
          {
            name: "Enterprise",
            href: links.enterprise,
            icon: (
              <AnimatedIcons
                color={colors.orangePastel01}
                animation="triangleRows"
                canPlay={canPlaySolutions === 1}
              />
            ),

            background: colors.orangePastel02,
            color: colors.orangeGray02,
          },

          {
            name: "Partners",
            href: links.partners,
            icon: (
              <AnimatedIcons
                color={colors.purplePastel01}
                animation="clusterDown"
                canPlay={canPlaySolutions === 2}
              />
            ),
            background: colors.purplePastel02,
            color: colors.purpleDark01,
          },
        ],
      },
      {
        section: "Customers",
        links: [
          {
            name: "Customers",
            href: links.customers,
          },
        ],
      },
      {
        section: "Resources",
        columns: [
          {
            section: "Learn",
            links: [
              {
                name: "Blog",
                href: links.blog,
              },
              {
                name: "Events",
                href: links.events,
              },
              {
                name: "Product Demos",
                href: links.productDemo,
              },
              // {
              //   name: "Q&A",
              //   href: links.qa,
              // },
            ],
          },
          {
            section: "About",
            links: [
              {
                name: "Careers",
                href: links.careers,
              },
              {
                name: "Community",
                href: links.community,
              },
              {
                name: "Developers",
                href: links.developers,
              },
            ],
          },
        ],
      },
    ],
    [canPlayProducts, canPlaySolutions]
  );

  const allLinks = useMemo(
    () =>
      SITE_LNKS.map((linkSection) => {
        return linkSection.links?.[0] && linkSection.links.length === 1 ? (
          <LinkButton
            className="header-link"
            key={linkSection.links[0].name}
            to={linkSection.links[0].href}
            variant="empty"
            dark={menuDark}
          >
            {linkSection.section}
          </LinkButton>
        ) : (
          <DropDown
            className="header-link"
            setCanPlayIndex={linkSection.animationHandler}
            key={linkSection.section}
            sectionData={linkSection}
          />
        );
      }),
    [SITE_LNKS, menuDark]
  );

  return (
    <Wrapper ref={wrapperEl}>
      {allBanners.length > 0 ? <Banner banners={banners} /> : null}
      <Inner>
        <Left>
          <StyledUniversalLink
            to={links.home}
            ariaLabel="to campfire homepage"
            $letterColors={letterColors}
          >
            <Logo
              width="268"
              height="53"
              viewBox="0 0 268 53"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g className="header-link" ref={logoLetters}>
                <path
                  className="logo-letters"
                  d="M191.889 15.6827H197.776C198.081 15.6827 198.329 15.9299 198.329 16.2349V41.0178H203.012V16.2349C203.012 15.9299 203.259 15.6827 203.564 15.6827H214.444C215.054 15.6827 215.548 16.1771 215.548 16.7871V41.0179H220.231V15.6827C220.231 13.5614 218.68 11.5137 215.7 11.5137L203.564 11.5136C203.259 11.5136 203.012 11.2664 203.012 10.9614V8.05972C203.012 6.61573 203.305 5.60104 203.89 5.01564C204.475 4.39121 205.392 4.079 206.641 4.079H210.805V0.0397339H205.529C203.109 0.0397339 201.295 0.703189 200.085 2.0301C198.914 3.31798 198.329 5.23029 198.329 7.76702V10.9614C198.329 11.2664 198.081 11.5136 197.776 11.5136H191.889V15.6827Z"
                  fill={letterColors}
                />
                <path
                  className="logo-letters"
                  d="M234.548 15.6864C233.416 15.6864 232.421 16.1547 231.562 17.0914C230.743 17.989 230.099 18.8235 229.63 20.1504C229.201 21.4773 228.987 22.7652 228.987 24.014V41.0253H224.303V11.5211H228.987V14.2212C228.987 14.7384 229.565 14.8825 229.865 14.426C230.254 13.8329 230.722 13.3133 231.27 12.8675C232.323 11.9699 233.553 11.5211 234.958 11.5211H239.055V15.6864H234.548Z"
                  fill={letterColors}
                />
                <path
                  className="logo-letters"
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M179.678 11.0456C182.02 11.0456 184.147 11.7286 186.059 13.0945C187.971 14.4214 189.454 16.2557 190.508 18.5973C191.601 20.8998 192.147 23.4561 192.147 26.266C192.147 29.154 191.601 31.7493 190.508 34.0518C189.454 36.3544 187.971 38.1692 186.059 39.4961C184.147 40.823 182 41.4864 179.62 41.4864H177.747C176.263 41.4864 174.78 41.0376 173.297 40.14C172.428 39.6136 171.672 39.0134 171.031 38.3393C170.389 37.6653 169.844 38.0252 169.844 38.748V52.9603H165.16V11.5139H169.844V13.8326C169.844 14.5999 170.374 14.91 171.018 14.2441C171.662 13.5783 172.422 12.9804 173.297 12.4506C174.82 11.5139 176.342 11.0456 177.864 11.0456H179.678ZM177.22 15.2019C175.815 15.2019 174.507 15.6703 173.297 16.6069C172.088 17.5435 171.132 18.8509 170.429 20.5291C169.766 22.2072 169.434 24.1195 169.434 26.266C169.434 28.4125 169.766 30.3248 170.429 32.0029C171.132 33.6811 172.088 34.9885 173.297 35.9251C174.507 36.8618 175.815 37.3301 177.22 37.3301H179.503C180.83 37.3301 182.078 36.8618 183.249 35.9251C184.459 34.9885 185.435 33.6811 186.176 32.0029C186.918 30.3248 187.289 28.4125 187.289 26.266C187.289 24.1195 186.918 22.2072 186.176 20.5291C185.435 18.8509 184.459 17.5435 183.249 16.6069C182.078 15.6703 180.83 15.2019 179.503 15.2019H177.22Z"
                  fill={letterColors}
                />
                <path
                  className="logo-letters"
                  d="M76.2466 41.4864C73.5928 41.4864 71.3097 40.784 69.3974 39.379C67.4851 37.935 66.0216 36.0617 65.007 33.7592C64.0313 31.4566 63.5435 28.9589 63.5435 26.266C63.5435 23.5732 64.0508 21.0755 65.0655 18.7729C66.1192 16.4313 67.6022 14.558 69.5145 13.153C71.4268 11.7481 73.6904 11.0456 76.3052 11.0456H78.354C80.3834 11.0456 82.2177 11.5529 83.8568 12.5676C85.5349 13.5433 86.8814 14.9092 87.896 16.6654C88.9107 18.3826 89.4766 20.334 89.5937 22.5195H84.9105C84.7154 20.451 84.0129 18.7143 82.8031 17.3094C81.6323 15.9044 80.1493 15.2019 78.354 15.2019H76.2466C74.5685 15.2019 73.1245 15.7093 71.9147 16.724C70.7439 17.7387 69.8658 19.0851 69.2804 20.7633C68.695 22.4024 68.4023 24.2366 68.4023 26.266C68.4023 28.2564 68.695 30.1101 69.2804 31.8273C69.8658 33.5055 70.7439 34.8519 71.9147 35.8666C73.1245 36.8423 74.549 37.3301 76.1881 37.3301H78.354C80.0712 37.3301 81.4957 36.6862 82.6275 35.3983C83.7592 34.1104 84.5203 32.4518 84.9105 30.4224H89.5937C89.3205 32.4127 88.6961 34.2665 87.7204 35.9837C86.7448 37.6618 85.4374 39.0082 83.7983 40.0229C82.1982 40.9986 80.3639 41.4864 78.2955 41.4864H76.2466Z"
                  fill={letterColors}
                />
                <path
                  className="logo-letters"
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M243.772 27.6503C243.743 27.3041 244.021 27.0133 244.368 27.0133H264.954C265.188 22.974 264.954 20.9659 263.725 18.0119C262.71 15.9044 261.324 14.2263 259.568 12.9774C257.812 11.6895 255.841 11.0456 253.656 11.0456H251.197C249.168 11.0456 247.197 11.6895 245.284 12.9774C243.372 14.2263 241.811 16.002 240.601 18.3046C239.43 20.6071 238.845 23.2024 238.845 26.0904C238.845 29.0174 239.391 31.6517 240.484 33.9933C241.577 36.3349 243.079 38.1692 244.992 39.4961C246.904 40.823 248.992 41.4864 251.256 41.4864H253.656C255.646 41.4864 257.461 41.0376 259.1 40.14C260.739 39.2424 262.066 38.0326 263.081 36.5105C264.095 34.9885 264.759 32.6924 264.954 30.8972H260.446C260.134 32.5363 259.295 34.5592 258.046 35.691C256.836 36.7837 255.353 37.3301 253.597 37.3301H251.314C249.909 37.3301 248.621 36.8618 247.45 35.9251C246.319 34.9885 245.402 33.6616 244.699 31.9444C244.209 30.6749 243.899 29.2105 243.772 27.6503ZM244.676 22.974C244.298 22.974 244.011 22.6321 244.099 22.2646C244.265 21.5668 244.485 20.9308 244.758 20.2949C245.499 18.6558 246.455 17.4069 247.626 16.5484C248.797 15.6507 250.046 15.2019 251.373 15.2019H253.656C254.865 15.2019 255.978 15.5727 256.992 16.3142C258.046 17.0167 258.866 17.9728 259.451 19.1827C259.979 20.2053 260.298 21.2321 260.407 22.3503C260.441 22.6909 260.164 22.974 259.821 22.974H244.676Z"
                  fill={letterColors}
                />
                <path
                  className="logo-letters"
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M94.02 39.2619C95.7372 40.7449 98.0397 41.4864 100.928 41.4864C103.113 41.4864 105.104 40.9986 106.899 40.0229C108.018 39.3904 109.455 38.2325 110.129 37.332C110.252 37.1666 110.355 37.0786 110.44 37.0409C110.709 36.9212 110.786 37.3103 110.854 37.6486C110.869 37.7261 110.884 37.8011 110.9 37.8665C111.363 39.7014 113.056 41.0181 114.951 41.0181H119.769C122.166 41.0181 123.915 39.0062 123.915 36.9863V25.4162C123.915 23.7381 124.188 22.138 124.734 20.6159C125.32 19.0939 126.12 17.8014 127.135 16.8648C128.149 15.8891 129.281 15.1973 130.53 15.1973H132.696C133.672 15.1973 134.967 15.8013 135.886 16.6896C136.743 17.5186 137.089 18.3643 137.423 19.3285C137.817 20.4667 137.806 21.3262 137.794 22.3666V22.3695C137.791 22.5587 137.789 22.754 137.789 22.958V41.0181H142.472V25.4162C142.472 23.6991 142.745 22.0794 143.292 20.5574C143.877 19.0354 144.677 17.7412 145.692 16.8046C146.706 15.8679 147.858 15.1957 149.146 15.1957H151.253C152.229 15.1957 153.524 15.7996 154.443 16.6879C155.251 17.4691 155.512 18.1363 155.905 19.1386L155.98 19.3285C156.432 20.476 156.422 21.3434 156.409 22.3991C156.407 22.5791 156.405 22.7646 156.405 22.958V41.0181H161.088V21.817C161.088 19.7876 160.697 17.9533 159.917 16.3142C159.175 14.6751 158.122 13.3872 156.756 12.4506C155.39 11.5139 153.829 11.0456 152.073 11.0456H149.79C148.228 11.0456 146.687 11.592 145.165 12.6847C143.993 13.5172 143.041 14.5079 142.308 15.657C142.064 16.0387 141.474 16.0397 141.241 15.6513C140.459 14.3475 139.562 13.3 138.55 12.5091C137.301 11.5334 135.662 11.0456 133.633 11.0456H131.291C129.808 11.0456 128.364 11.5725 126.959 12.6262C126.197 13.1975 125.55 13.8376 125.018 14.5466C124.486 15.2555 123.915 14.8374 123.915 14.2492V11.5139H119.232V36.144C119.232 36.8531 118.775 37.332 118.078 37.332H116.283C115.731 37.332 115.16 36.8492 115.16 36.2067L115.211 22.2268C115.211 20.0413 114.802 18.1094 113.982 16.4313C113.163 14.7141 111.992 13.3872 110.47 12.4506C108.948 11.5139 107.172 11.0456 105.143 11.0456H102.684C100.811 11.0456 99.0739 11.4554 97.4738 12.2749C95.8737 13.0945 94.5664 14.2653 93.5517 15.7873C92.537 17.2704 91.7955 19.5261 91.7955 22.5187H96.5372C96.5372 19.6639 97.3372 17.7387 98.469 16.724C99.6008 15.7093 101.006 15.2019 102.684 15.2019H105.143C106.157 15.2019 107.074 15.4946 107.894 16.08C108.713 16.6654 109.357 17.485 109.826 18.5387C110.294 19.5925 110.528 20.7828 110.528 22.1097C110.528 22.3124 110.382 22.4853 110.182 22.5187L102.098 23.8659C98.7422 24.4513 96.1274 25.4855 94.2541 26.9685C92.3809 28.4515 91.4442 30.559 91.4442 33.2908C91.4442 35.7885 92.3028 37.7789 94.02 39.2619ZM109.878 26.4507C110.217 26.3898 110.528 26.6501 110.528 26.9942V28.1978C110.528 30.9687 109.65 33.1933 107.894 34.8714C106.177 36.5105 103.991 37.3301 101.337 37.3301C99.8545 37.3301 98.6446 36.9398 97.708 36.1593C96.7714 35.3788 96.3031 34.3446 96.3031 33.0567C96.3031 31.3785 96.8494 30.1687 97.9422 29.4272C99.0349 28.6857 100.752 28.1003 103.094 27.671L109.878 26.4507Z"
                  fill={letterColors}
                />
                <path
                  className="logo-letters"
                  d="M265.961 10.7639C265.398 10.7639 264.881 11.1379 264.881 11.8965C264.881 12.655 265.392 13.0291 265.961 13.0291C266.478 13.0291 266.867 12.7288 266.92 12.2863H266.578C266.53 12.555 266.288 12.7341 265.961 12.7341C265.529 12.7341 265.224 12.4391 265.224 11.8965C265.224 11.3539 265.529 11.0589 265.961 11.0589C266.283 11.0589 266.525 11.2327 266.572 11.5067H266.92C266.857 11.0589 266.467 10.7639 265.961 10.7639Z"
                  fill={letterColors}
                />
                <path
                  className="logo-letters"
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M265.946 13.9773C264.792 13.9773 263.891 13.0765 263.891 11.9228C263.891 10.7639 264.792 9.86838 265.946 9.86838C267.104 9.86838 268 10.7639 268 11.9228C268 13.0765 267.104 13.9773 265.946 13.9773ZM264.197 11.9228C264.202 12.9184 264.966 13.6875 265.946 13.6875C266.931 13.6875 267.689 12.9184 267.694 11.9228C267.689 10.9272 266.931 10.1581 265.946 10.1581C264.966 10.1581 264.202 10.9272 264.197 11.9228Z"
                  fill={letterColors}
                />
                <path
                  className="logo-letters"
                  d="M217.819 6.85322C219.354 6.85322 220.597 5.60951 220.597 4.07532C220.597 2.54113 219.354 1.29742 217.819 1.29742C216.285 1.29742 215.042 2.54113 215.042 4.07532C215.042 5.60951 216.285 6.85322 217.819 6.85322Z"
                  fill={letterColors}
                />
              </g>
              <path
                className="logo-fire header-link"
                d="M29.4457 33.8945L45.23 18.1102L48.1764 21.0566L32.3682 36.8648H54.7246V41.0317H27.3623L34.3071 41.0316C32.7401 41.0316 31.6523 40.144 30.5994 39.2848C29.6119 38.479 28.655 37.6982 27.3623 37.6982C26.0696 37.6982 25.1128 38.479 24.1252 39.2848C23.0723 40.144 21.9845 41.0317 20.4176 41.0317L27.3623 41.0317H0V36.8648H22.3564L6.54818 21.0566L9.49458 18.1102L25.2789 33.8945L25.2789 11.5279H29.4457L29.4457 33.8945Z"
                fill="#FF862F"
              />
            </Logo>
          </StyledUniversalLink>
          {allLinks}
        </Left>
        <Right className="header-right">
          <LinkButton className="header-link" to={links.logIn} dark={menuDark}>
            Log in
          </LinkButton>
          <StyledSecondary to={links.getStarted} variant={color}>
            Get Started
          </StyledSecondary>
          <HamburgerWrapper className="desktop-hamburger">
            <ResponsiveDropDown
              open={open}
              setOpen={setOpen}
              sectionData={hamburgerData}
            />
          </HamburgerWrapper>
        </Right>
      </Inner>
    </Wrapper>
  );
}

const Wrapper = styled.header`
  position: fixed;
  left: 0;
  color: white;
  display: grid;
  place-items: center;
  width: 100vw;
  z-index: 100;

  ${fresponsive(css`
    height: 110px;
  `)}
`;

const Inner = styled.div`
  max-width: 1220px;
  display: flex;
  justify-content: space-between;
  ${fresponsive(css`
    width: 1220px;
    margin-top: 50px;
  `)}
`;

const Left = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;

const StyledUniversalLink = styled(UniversalLink)<{ $letterColors: string }>`
  svg {
    path {
      fill: ${({ $letterColors }) => $letterColors};
    }
  }
`;

const Logo = styled.svg`
  .logo-fire {
    fill: ${colors.orangeSaturated01};
  }
  ${fresponsive(css`
    width: 182px;
    height: auto;
  `)}
`;

const Right = styled.div`
  display: flex;
  align-items: center;
  position: relative;

  ${fresponsive(css`
    gap: 42.5px;
  `)}
`;

const HamburgerWrapper = styled.div`
  position: absolute;
  ${fresponsive(css`
    width: 74px;
    height: 50px;
    right: -85px;
    opacity: 0;
    pointer-events: none;
  `)}

  ${ftablet(css`
    display: none;
  `)}

  ${fmobile(css`
    display: none;
  `)}
`;

const StyledSecondary = styled(Secondary)`
  position: relative;
  z-index: 101;
`;
